import logger from "@/lib/logger";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Barcode from "react-barcode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillLabels() {
  const { billId } = useParams();
  const [bill,    setBill]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [perRow,  setPerRow]  = useState(4); // 4 per row default

  useEffect(() => {
    axios.get(`${API}/bills/${billId}`)
      .then(r => setBill(r.data))
      .catch(err => { logger.error("[BillLabels]", err?.message); setError("Bill not found"); })
      .finally(() => setLoading(false));
  }, [billId]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full" />
    </div>
  );

  if (error || !bill) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-slate-500">{error || "Bill not found"}</p>
    </div>
  );

  // Generate all labels from range (client-side — no DB call for each)
  const labels = Array.from({ length: bill.quantity }, (_, i) => ({
    n:       i + 1,
    barcode: `${bill.bill_no}-${String(i + 1).padStart(4, "0")}`,
  }));

  // Width settings per perRow setting
  const barcodeWidth  = perRow <= 2 ? 1.8 : perRow === 3 ? 1.4 : 1.1;
  const barcodeHeight = perRow <= 2 ? 50  : perRow === 3 ? 40  : 32;
  const fontSize      = perRow <= 2 ? 11  : perRow === 3 ? 10  : 9;

  return (
    <>
      {/* ===== Print Toolbar ===== */}
      <div className="no-print fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <div>
          <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            {bill.bill_no} — {bill.customer_name} · {bill.product_name}
          </p>
          <p className="text-xs text-slate-400">
            {bill.quantity} labels &nbsp;|&nbsp;
            <span className="font-mono text-green-700">{bill.barcode_start}</span>
            &nbsp;→&nbsp;
            <span className="font-mono text-green-700">{bill.barcode_end}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Per-row selector */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Per row:</span>
            {[2, 3, 4, 5].map(n => (
              <button key={n} onClick={() => setPerRow(n)}
                className={`text-xs font-semibold px-2 py-1 rounded-lg transition-colors ${
                  perRow === n ? "bg-green-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}>{n}</button>
            ))}
          </div>
          <button onClick={() => window.history.back()}
            className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1.5 border border-slate-200 rounded-lg">
            ← Back
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-colors"
            data-testid="bill-labels-print-btn">
            🖨️ Print All {bill.quantity} Labels
          </button>
        </div>
      </div>

      {/* ===== Labels Sheet ===== */}
      <div style={{ paddingTop: "66px", background: "#f8fafc", minHeight: "100vh" }}>
        <div style={{ maxWidth: "794px", margin: "0 auto", padding: "10px" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: `repeat(${perRow}, 1fr)`,
            gap: "4px",
          }}>
            {labels.map(label => (
              <div key={label.barcode} style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "4px",
                padding: "6px 4px 4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                pageBreakInside: "avoid",
                breakInside: "avoid",
              }}>
                {/* ONLY the barcode — narrow and clean */}
                <Barcode
                  value={label.barcode}
                  width={barcodeWidth}
                  height={barcodeHeight}
                  fontSize={fontSize}
                  margin={1}
                  background="#ffffff"
                  displayValue={false}
                />
                {/* Serial number only */}
                <p style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: `${fontSize}px`,
                  fontWeight: 700,
                  color: "#1e293b",
                  margin: "2px 0 0",
                  textAlign: "center",
                  letterSpacing: "0.01em",
                }}>
                  {label.barcode}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 6mm; }
          .no-print { display: none !important; }
          body { background: white !important; }
          div[style*="paddingTop"] { padding-top: 0 !important; background: white !important; }
        }
      `}</style>
    </>
  );
}
